import type { Track } from "../../event-experience/schemas";
import type { TrackSwitchRequest } from "../../track-switch/schemas";
import type { AttendeeRecord } from "../use-attendee-records";
import { formatRequestTime, type AdminRequestRow } from "./RequestList";

/**
 * How many people sit at each table, from the roster. Tables have no capacity
 * (the TNG-92 decision was switch requests without capacities), so this is
 * the size the organiser weighs; manual assignment shows none either.
 */
export function seatedByTrack(
  attendees: AttendeeRecord[],
): Map<string, number> {
  const counts = new Map<string, number>();
  for (const attendee of attendees) {
    if (attendee.trackId && attendee.status !== "withdrawn") {
      counts.set(attendee.trackId, (counts.get(attendee.trackId) ?? 0) + 1);
    }
  }
  return counts;
}

const seated = (count: number) =>
  count === 1 ? "1 person seated" : `${count} people seated`;

/**
 * Table switch requests as Requests-tab rows.
 *
 * The current table is read from the roster as it is now, not from the
 * request's `fromTrackId`: an organiser may have moved the person since they
 * asked. A table deleted since reads by the name the request kept.
 */
export function trackSwitchRows({
  requests,
  attendees,
  tracks,
  timeZone,
}: {
  requests: TrackSwitchRequest[];
  attendees: AttendeeRecord[];
  tracks: Track[];
  timeZone?: string;
}): AdminRequestRow[] {
  const attendeesById = new Map(attendees.map((a) => [a.id, a]));
  const tracksById = new Map(tracks.map((t) => [t.id, t]));
  const counts = seatedByTrack(attendees);

  const rows = requests.map((request): AdminRequestRow => {
    const attendee = attendeesById.get(request.attendeeId);
    const currentId = attendee ? attendee.trackId : request.fromTrackId;
    const current = currentId ? tracksById.get(currentId) : undefined;
    const target = tracksById.get(request.toTrackId);
    const open = request.status === "OPEN";
    return {
      type: "track-switch",
      id: request.attendeeId,
      open,
      person: attendee
        ? {
            attendeeId: attendee.id,
            name: attendee.name,
            role: attendee.role,
            company: attendee.company,
            avatar: attendee.avatar,
          }
        : null,
      personFallback: "Former attendee",
      current: {
        caption: "Current theme table",
        label: current?.name ?? "No theme table",
      },
      requested: {
        caption: "Asks for",
        label:
          target?.name ??
          (request.toTrackName
            ? `${request.toTrackName} (deleted)`
            : "A deleted theme table"),
        detail: open && target ? seated(counts.get(target.id) ?? 0) : undefined,
      },
      reason: request.reason,
      requestedAt: request.requestedAt?.toDate() ?? null,
      decidedAt: request.decidedAt?.toDate() ?? null,
      ...(open ? {} : outcome(request, timeZone)),
    };
  });

  return sortRequestRows(rows);
}

/**
 * Oldest open request first: it has waited longest. Decided ones newest
 * first, since the last few answers are what an organiser looks back for.
 * Shared by every request type, so a merged list reads the same way.
 */
export function sortRequestRows(rows: AdminRequestRow[]): AdminRequestRow[] {
  const asked = (row: AdminRequestRow) => row.requestedAt?.getTime() ?? 0;
  const decided = (row: AdminRequestRow) =>
    row.decidedAt?.getTime() ?? asked(row);
  return [...rows].sort((a, b) => {
    if (a.open !== b.open) return a.open ? -1 : 1;
    return a.open ? asked(a) - asked(b) : decided(b) - decided(a);
  });
}

function outcome(
  request: TrackSwitchRequest,
  timeZone?: string,
): Pick<AdminRequestRow, "outcome" | "outcomeTone"> {
  const when = request.decidedAt
    ? ` ${formatRequestTime(request.decidedAt.toDate(), timeZone)}`
    : "";
  switch (request.status) {
    case "ACCEPTED":
      return { outcome: `Accepted${when}`, outcomeTone: "positive" };
    case "DECLINED":
      return { outcome: `Declined${when}`, outcomeTone: "negative" };
    default:
      return {
        outcome:
          request.cancelReason === "TRACK_DELETED"
            ? `Cancelled${when}: the theme table was deleted`
            : `Cancelled${when}: theme table requests were switched off`,
        outcomeTone: "neutral",
      };
  }
}
